'use strict';

Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.findElbowPoint = exports.getErrorMargins = exports.getLinearIncrement = exports.getSlope = exports.addPoint = exports.createEmptyScreePlot = undefined;

var _lodash = require('lodash');

var _documentOrderAlgorithmConstants = require('../documentOrderAlgorithmConstants');

// Lib
var createEmptyScreePlot = exports.createEmptyScreePlot = function createEmptyScreePlot(autoClusterDistance) {
    return {
        points: [],
        currMaxHeight: 0,
        autoClusterDistance: autoClusterDistance
    };
};

// Constants


var createScreePlotPoint = function createScreePlotPoint(_ref) {
    var x = _ref.x,
        height = _ref.height,
        variance = _ref.variance;
    return { x: x, height: height, variance: variance };
};

// √
var addPoint = exports.addPoint = function addPoint(screePlot, variance) {
    // "height" is the cumulative distance between clusters which have been merged.
    // It's used as the measure of the variance between clusters in the dendrogram.
    // This is because a more traditional measurement of variance is impossible since
    // the distance function is non-euclidean which makes finding a centroid for each cluster impossible
    var height = screePlot.currMaxHeight + variance;

    var point = createScreePlotPoint({ height: height, x: screePlot.points.length, variance: variance });
    screePlot.points.push(point);
    screePlot.currMaxHeight = height;

    return screePlot;
};

var getSlope = exports.getSlope = function getSlope(dx, dy) {
    return Math.atan(dy / dx);
};

/**
 * Finds the change in height and divides it by the number of points to ensure that the width of the graph
 * is the same as the height.
 * This is important for the elbow point calculations to be accurate.
 */
var getLinearIncrement = exports.getLinearIncrement = function getLinearIncrement(points) {
    if (points.length === 1) return points[0].variance;
    return (points[points.length - 1].height - points[0].height) / points.length;
};

var getErrorMargins = exports.getErrorMargins = function getErrorMargins(points) {
    // Finds all the error margins when creating a linear line from the origin to the point and then the point
    // to the curve end
    var maxHeight = points[points.length - 1].height;
    var maxX = points.length - 1;
    var dx = getLinearIncrement(points);
    return points.slice(1, -1).map(function (point, index) {
        // Calculate the gradients to find the error between the points, then choose the point with the min error
        // Lines will be of the form:
        //  y = m1 * x    and
        //  y = m2 * x + c2  (c is a negative number)

        // Therefore:
        // m1 = height / (index * dx)
        // and
        // m2 = (max height - height) / (max index * dx - index * dx)
        // c = max height - (max index * dx) * ((max height - height) / ((max index * dx) - (index * dx)))

        // The first line will intersect the origin so no need to worry about a c
        var height = point.height;
        var currX = index + 1;
        var m1 = height / currX;
        var line1Fn = function line1Fn(x) {
            return m1 * x;
        };

        var m2 = (maxHeight - height) / (maxX - currX);
        var c2 = maxHeight - maxX * (maxHeight - height) / (maxX - currX);
        var line2Fn = function line2Fn(x) {
            return m2 * x + c2;
        };

        // The total error difference between points using the functions
        var cumulativeError = 0;
        for (var i = 0; i < currX; i++) {
            var actualHeight = points[0].height;
            cumulativeError += Math.abs(actualHeight - line1Fn(i));
        }

        for (var _i = currX; _i < maxX; _i++) {
            var _actualHeight = points[0].height;
            cumulativeError += Math.abs(_actualHeight - line2Fn(_i));
        }

        // Used to determine if the line is too linear
        var theta1 = getSlope(currX * dx, height);
        var theta2 = getSlope((maxX - currX) * dx, maxHeight - height);

        return {
            cumulativeError: cumulativeError,
            index: currX,
            dTheta: theta2 - theta1
        };
    });
};

// √
var findElbowPoint = exports.findElbowPoint = function findElbowPoint(screePlot) {
    var errorMargins = getErrorMargins(screePlot.points);
    var minErrorMargin = (0, _lodash.minBy)(errorMargins, function (errorMargin) {
        return errorMargin.cumulativeError;
    });

    var elbowPoint = screePlot.points[screePlot.points.length - 1];
    var dTheta = 0;
    if (minErrorMargin) {
        // If we don't have an elbow point just use the last value
        elbowPoint = screePlot.points[minErrorMargin.index];
        dTheta = minErrorMargin.dTheta;
    }

    // If graph is too linear, change it to the last point so everything is split
    var tooLinear = dTheta < _documentOrderAlgorithmConstants.MIN_CHANGE_IN_SCREE_PLOT_SLOPE;
    if (tooLinear) {
        elbowPoint = screePlot.points[screePlot.points.length - 1];
    }

    return {
        elbowPoint: elbowPoint,
        errorMargins: errorMargins,
        tooLinear: tooLinear,
        points: screePlot.points
    };
};


//////////////////
// WEBPACK FOOTER
// ./src/common/documentOrder/structures/screePlot.js
// module id = 792
// module chunks = 0